/**
 * Every field of the groups, in field order. The group does not matter:
 * a field of order 1 comes before a field of order 2 from another group.
 * @function fieldsByOrder
 * @since 1.0.0
 */
export function fieldsByOrder(groups) {
    return (groups !== null && groups !== void 0 ? groups : [])
        .flatMap((group) => { var _a; return (_a = group.fields) !== null && _a !== void 0 ? _a : []; })
        .sort((a, b) => { var _a, _b; return ((_a = a.order) !== null && _a !== void 0 ? _a : 0) - ((_b = b.order) !== null && _b !== void 0 ? _b : 0); });
}
/**
 * The form name of a field, unique per line and voucher
 * @function unitFieldKey
 * @since 1.0.0
 */
export function unitFieldKey(lineId, unit, fieldId) {
    return `${lineId}__${unit}__${fieldId}`;
}
/**
 * The Object scalar comes back as JSON text
 * @function parseUnitFieldValues
 * @since 1.0.0
 */
export function parseUnitFieldValues(values) {
    return (values !== null && values !== void 0 ? values : []).map((entry) => {
        var _a;
        if (typeof entry !== 'string') {
            return entry !== null && entry !== void 0 ? entry : {};
        }
        try {
            return (_a = JSON.parse(entry)) !== null && _a !== void 0 ? _a : {};
        }
        catch (_b) {
            return {};
        }
    });
}
/**
 * @function isUnitAnswerEmpty
 * @since 1.0.0
 */
export function isUnitAnswerEmpty(field, value) {
    if (value == null) {
        return true;
    }
    if (typeof value === 'string') {
        // An emptied rich text field still holds markup
        return value.replace(/<[^>]*>/g, '').trim() === '';
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (field.type === 'CHECKBOX') {
        return value !== true;
    }
    return false;
}
/**
 * Collects the answers of one line from the form values
 * @function readLineAnswers
 * @since 1.0.0
 */
export function readLineAnswers(formFields, line, groups) {
    const fields = groups.flatMap((group) => { var _a; return (_a = group.fields) !== null && _a !== void 0 ? _a : []; });
    return Array.from({ length: line.voucherCount }, (_, unit) => {
        const answers = {};
        for (const field of fields) {
            let value = formFields === null || formFields === void 0 ? void 0 : formFields[unitFieldKey(line.id, unit, field.id)];
            // An untouched yes/no toggle shows "No" but holds no value
            if (field.type === 'YESNO' && (value == null || value === '')) {
                value = '0';
            }
            if (!isUnitAnswerEmpty(field, value)) {
                answers[field.id] = value;
            }
        }
        return answers;
    });
}
/**
 * Required fields left empty, as "unit:field" keys
 * @function findMissingRequired
 * @since 1.0.0
 */
export function findMissingRequired(answers, groups) {
    const required = groups
        .flatMap((group) => { var _a; return (_a = group.fields) !== null && _a !== void 0 ? _a : []; })
        .filter((field) => field.required);
    return answers.flatMap((unitAnswers, unit) => required
        .filter((field) => isUnitAnswerEmpty(field, unitAnswers[field.id]))
        .map((field) => `${unit}:${field.id}`));
}
/**
 * @function toUnitsInput
 * @since 1.0.0
 */
export function toUnitsInput(answers) {
    return answers.map((unitAnswers) => ({
        fieldValues: Object.entries(unitAnswers).map(([fieldId, value]) => ({
            fieldId,
            value: JSON.stringify({ value })
        }))
    }));
}
/**
 * approveDeal refused the order: a required answer is missing on a voucher
 * @function isVoucherFieldsMissingError
 * @since 1.0.0
 */
export function isVoucherFieldsMissingError(error) {
    var _a;
    const graphQLErrors = error === null || error === void 0 ? void 0 : error.graphQLErrors;
    return ((_a = graphQLErrors === null || graphQLErrors === void 0 ? void 0 : graphQLErrors.some((entry) => { var _a; return ((_a = entry === null || entry === void 0 ? void 0 : entry.extensions) === null || _a === void 0 ? void 0 : _a.description) === 'VOUCHER_FIELDS_MISSING'; })) !== null && _a !== void 0 ? _a : false);
}
